import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useCustomers } from '@/hooks/useCustomers';
import { safeArray, safeMap } from '@/utils/safeArray';

interface SimpleCustomerDropdownProps {
  value?: string;
  onChange: (customerId: string) => void;
  onCreateNew?: () => void;
  placeholder?: string;
  className?: string;
}

const SimpleCustomerDropdown: React.FC<SimpleCustomerDropdownProps> = ({
  value,
  onChange,
  onCreateNew,
  placeholder = "Select customer...",
  className
}) => {
  const { customers, isLoading } = useCustomers();
  
  // Use safe array handling to prevent iteration errors
  const safeCustomers = safeArray(customers);
  
  const selectedCustomer = safeCustomers.find(c => c.id === value);

  if (isLoading) {
    return (
      <div className="flex gap-2">
        <Select disabled>
          <SelectTrigger className={className}>
            <SelectValue placeholder="Loading customers..." />
          </SelectTrigger>
        </Select>
        {onCreateNew && (
          <Button size="sm" variant="outline" disabled>
            <Plus className="h-4 w-4" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex gap-2">
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={className}>
          <SelectValue placeholder={placeholder}>
            {selectedCustomer ? selectedCustomer.name : placeholder}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="bg-background border shadow-lg z-50">
          {safeMap(safeCustomers, (customer) => (
            <SelectItem key={customer.id} value={customer.id}>
              <div className="flex flex-col">
                <span className="font-medium">{customer.name}</span>
                {customer.email && (
                  <span className="text-sm text-muted-foreground">{customer.email}</span>
                )}
              </div>
            </SelectItem>
          ))}
          {safeCustomers.length === 0 && (
            <SelectItem value="no-customers" disabled>
              No customers found
            </SelectItem>
          )}
        </SelectContent>
      </Select>
      
      {onCreateNew && (
        <Button size="sm" variant="outline" onClick={onCreateNew}>
          <Plus className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

export default SimpleCustomerDropdown;